import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, countryFlag } from "../lib/utils";
import { Eye, X, Package, Truck, MapPin, Calendar, CaretRight, CheckCircle, Warning } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import BottomSheet from "../components/BottomSheet";
import OrderDetailBody from "../components/OrderDetailBody";

export default function ClientOrders() {
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/client/orders").then(({ data }) => setOrders(data));
  }, []);

  const STATUS_LABELS = { blocked: { label: t("blocked"), cls: "tf-status-error" } };
  const PAID = { label: t("order_paid"), cls: "tf-status-success" };

  const podCost = (o) => {
    const c = o.cost || {};
    // items_total nel backend include picking_fee → POD = items_total - picking_fee
    return (c.items_total || 0) - (c.picking_fee || 0);
  };

  return (
    <div className="space-y-6" data-testid="client-orders">
      <PageHeader
        eyebrow={t("orders")}
        title={t("orders_page_title")}
        subtitle={t("orders_page_subtitle")}
        chip="tf-chip-lime"
        icon={Package}
      />
      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead>
              <tr>
                <th>{t("orders_col_date")}</th>
                <th>{t("orders_col_order")}</th>
                <th>{t("orders_col_id")}</th>
                <th>{t("orders_col_status")}</th>
                <th>{t("orders_col_destination")}</th>
                <th className="text-right">{t("orders_col_pod")}</th>
                <th className="text-right">{t("orders_col_shipping")}</th>
                <th className="text-right">{t("orders_col_picking")}</th>
                <th className="text-right">{t("orders_col_total")}</th>
                <th>{t("orders_col_detail")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={10} className="text-center text-[var(--tf-muted)] py-10">{t("orders_no_orders")}</td></tr>
              )}
              {orders.map((o) => {
                const st = STATUS_LABELS[o.status] || PAID;
                return (
                  <tr key={o.id} data-testid={`order-${o.id}`}>
                    <td className="text-xs font-mono whitespace-nowrap">{fmtDate(o.myze_created_at || o.created_at, lang)}</td>
                    <td className="font-mono text-xs">{o.origin_id || "—"}</td>
                    <td className="font-mono text-xs">{o.external_order_number || o.myze_order_id || "—"}</td>
                    <td><span className={`tf-status-pill ${st.cls}`}>{st.label}</span></td>
                    <td className="font-mono text-xs">{o.shipping_country ? `${countryFlag(o.shipping_country)} ${o.shipping_country}` : "—"}</td>
                    <td className="text-right tf-numeric">{fmtEur(podCost(o))}</td>
                    <td className="text-right tf-numeric">{fmtEur(o.cost?.shipping || 0)}</td>
                    <td className="text-right tf-numeric">{fmtEur(o.cost?.picking_fee || 0)}</td>
                    <td className="text-right tf-numeric font-bold">{fmtEur(o.cost?.total || 0)}</td>
                    <td>
                      <button
                        onClick={() => setSelected(o)}
                        className="tf-btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                        data-testid={`detail-${o.id}`}
                      >
                        <Eye size={14} weight="bold" /> {t("orders_detail_btn")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="orders-mlist">
        {orders.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <Package size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {t("orders_no_orders")}
          </div>
        )}
        {orders.map((o) => {
          const isBlocked = o.status === "blocked";
          const st = STATUS_LABELS[o.status] || PAID;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setSelected(o)}
              className="tf-mrow text-left"
              data-testid={`order-card-${o.id}`}
            >
              <div
                className="tf-mrow-icon"
                style={{
                  background: isBlocked ? "#FBDDE1" : "#ECFCC9",
                  color: isBlocked ? "#A11534" : "#3E5A0A",
                }}
              >
                {isBlocked ? <Warning size={20} weight="fill" /> : <CheckCircle size={20} weight="fill" />}
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title">
                  <span className="truncate">{o.origin_id || o.external_order_number || o.myze_order_id || "—"}</span>
                  <span className={`tf-dot ${isBlocked ? "tf-dot-error" : "tf-dot-success"}`} title={st.label} />
                </div>
                <div className="tf-mrow-sub flex items-center gap-2">
                  <Calendar size={11} weight="bold" />
                  <span>{fmtDate(o.myze_created_at || o.created_at, lang)}</span>
                  <span className="opacity-40">·</span>
                  <MapPin size={11} weight="bold" />
                  <span>{o.shipping_country ? `${countryFlag(o.shipping_country)} ${o.shipping_country}` : "—"}</span>
                </div>
              </div>
              <div className="tf-mrow-value tf-numeric">{fmtEur(o.cost?.total || 0)}</div>
              <CaretRight size={14} weight="bold" className="tf-mrow-chev" />
            </button>
          );
        })}
      </div>

      {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} lang={lang} t={t} />}
    </div>
  );
}

function OrderDetailModal({ order, onClose, lang, t }) {
  return (
    <BottomSheet
      open={!!order}
      onClose={onClose}
      subtitle={t("order_detail_title")}
      title={order.origin_id || order.external_order_number || order.myze_order_id}
      testId="order-detail-modal"
    >
      <OrderDetailBody order={order} lang={lang} />
    </BottomSheet>
  );
}
